import { defineStore, type StateTree } from 'pinia';
import { inject } from 'vue';
import API from '@/classes/API';
import type {
	List_TipiSegnalazioneObject,
	List_EntiPerCategoriaObject,
	List_PosizioniObject,
	List_DichiarazioniObject,
	List_SegnalazioniObject,
	List_Documenti,
	EnteGES,
	EntiObject,
} from '@/types/list';
import { LISTA_CATEGORIE_ENTE_CATEGORIA } from '@/types/list';

// Cripto i dati prima di salvarli in session storage
import { CryptoXOR } from '@/classes/CryptoUtils';
const cryptoInstance = new CryptoXOR();

export const useListStore = defineStore('list', {
	persist: {
		storage: sessionStorage,
		serializer: {
			serialize: (data): string => {
				return cryptoInstance.encrypt(data);
			},
			deserialize: (data): StateTree => {
				return cryptoInstance.decrypt(data) as Record<string, any>;
			}
		}
	},
	state: () => ({
		apiInstance: inject('apiInstance') as API,
		tipiSegnalazione: [] as List_TipiSegnalazioneObject[],

		entiSportelloTerritoriale: [] as EntiObject[],

		entiNuovoAllacciamentoSegnalazione: [] as List_EntiPerCategoriaObject[],
		entiRecuperoCreditiSegnalazione: [] as List_EntiPerCategoriaObject[],
		entiRifiutiSegnalazione: [] as List_EntiPerCategoriaObject[],
		entiParcheggiMobilitySegnalazione: [] as List_EntiPerCategoriaObject[],
		entiPubblicitaAffissioniSegnalazione: [] as List_EntiPerCategoriaObject[],
		entiImmobiliSegnalazione: [] as List_EntiPerCategoriaObject[],
		entiOccupazioneSuoloSegnalazione: [] as List_EntiPerCategoriaObject[],
		entiImpostaSoggiornoSegnalazione: [] as List_EntiPerCategoriaObject[],

		entiNuovoAllacciamento: [] as List_EntiPerCategoriaObject[],
		entiAffissioneManifesti: [] as List_EntiPerCategoriaObject[],
		entiLocandine: [] as List_EntiPerCategoriaObject[],
		entiIstanzaContribuente: [] as List_EntiPerCategoriaObject[],
		entiDelegaImpostaSoggiorno: [] as List_EntiPerCategoriaObject[],
		entiAbilitazioneStruttura: [] as List_EntiPerCategoriaObject[],
		entiAbbonamentoParcheggi: [] as List_EntiPerCategoriaObject[],
		entiPagamento: [] as EnteGES[],
		entiPagamentoPrecompilato: [] as EnteGES[],
		posizioni: [] as List_PosizioniObject[],
		loadingPosizioni: false as Boolean,
		documentiRappresentanza: [] as List_Documenti[], // sono i documenti di una specifica rappresentanza (tutti i documenti di tutte le posizioni per quell'ente)
		dichiarazioni: [] as List_DichiarazioniObject[],
		loadingDichiarazioni: false as Boolean,
		segnalazioni: [] as List_SegnalazioniObject[],
		entiNuovoMezzoPermanente: [] as List_EntiPerCategoriaObject[],
	}),
	getters: {
		isTipiSegnalazioneLoaded: (state) => state.tipiSegnalazione && state.tipiSegnalazione.length > 0,
		getTipiSegnalazione: (state) => state.tipiSegnalazione,

		isEntiSportelloTerritorialeLoaded: (state) =>
			state.entiSportelloTerritoriale && state.entiSportelloTerritoriale.length > 0,
		getEntiSportelloTerritoriale(state) {
			return state.entiSportelloTerritoriale;
		},

		isEntiNuovoAllacciamentoSegnalazioneLoaded: (state) =>
			state.entiNuovoAllacciamentoSegnalazione && state.entiNuovoAllacciamentoSegnalazione.length > 0,
		getEntiNuovoAllacciamentoSegnalazione(state) {
			return state.entiNuovoAllacciamentoSegnalazione;
		},

		isEntiRecuperoCreditiSegnalazioneLoaded: (state) =>
			state.entiRecuperoCreditiSegnalazione && state.entiRecuperoCreditiSegnalazione.length > 0,
		getEntiRecuperoCreditiSegnalazione(state) {
			return state.entiRecuperoCreditiSegnalazione;
		},

		isEntiRifiutiSegnalazioneLoaded: (state) => state.entiRifiutiSegnalazione && state.entiRifiutiSegnalazione.length > 0,
		getEntiRifiutiSegnalazione(state) {
			return state.entiRifiutiSegnalazione;
		},

		isEntiParcheggiMobilitySegnalazioneLoaded: (state) =>
			state.entiParcheggiMobilitySegnalazione && state.entiParcheggiMobilitySegnalazione.length > 0,
		getEntiParcheggiMobilitySegnalazione(state) {
			return state.entiParcheggiMobilitySegnalazione;
		},

		isEntiPubblicitaAffissioniSegnalazioneLoaded: (state) =>
			state.entiPubblicitaAffissioniSegnalazione && state.entiPubblicitaAffissioniSegnalazione.length > 0,
		getEntiPubblicitaAffissioniSegnalazione(state) {
			return state.entiPubblicitaAffissioniSegnalazione;
		},

		isEntiImmobiliSegnalazioneLoaded: (state) => state.entiImmobiliSegnalazione && state.entiImmobiliSegnalazione.length > 0,
		getEntiImmobiliSegnalazione(state) {
			return state.entiImmobiliSegnalazione;
		},

		isEntiOccupazioneSuoloSegnalazioneLoaded: (state) =>
			state.entiOccupazioneSuoloSegnalazione && state.entiOccupazioneSuoloSegnalazione.length > 0,
		getEntiOccupazioneSuoloSegnalazione(state) {
			return state.entiOccupazioneSuoloSegnalazione;
		},

		isEntiImpostaSoggiornoSegnalazioneLoaded: (state) =>
			state.entiImpostaSoggiornoSegnalazione && state.entiImpostaSoggiornoSegnalazione.length > 0,
		getEntiImpostaSoggiornoSegnalazione(state) {
			return state.entiImpostaSoggiornoSegnalazione;
		},

		isEntiNuovoAllacciamentoLoaded: (state) => state.entiNuovoAllacciamento && state.entiNuovoAllacciamento.length > 0,
		getEntiNuovoAllacciamento(state) {
			return state.entiNuovoAllacciamento;
		},
		isEntiAbbonamentoParcheggiLoaded: (state) => state.entiAbbonamentoParcheggi && state.entiAbbonamentoParcheggi.length > 0,
		getEntiAbbonamentoParcheggi(state) {
			return state.entiAbbonamentoParcheggi;
		},
		isEntiLocandineLoaded: (state) => state.entiLocandine && state.entiLocandine.length > 0,
		getEntiLocandine(state) {
			return state.entiLocandine;
		},

		isEntiIstanzaContribuenteLoaded: (state) => state.entiIstanzaContribuente && state.entiIstanzaContribuente.length > 0,
		getEntiIstanzaContribuente(state) {
			return state.entiIstanzaContribuente;
		},

		isEntiDelegaImpostaSoggiornoLoaded: (state) => state.entiDelegaImpostaSoggiorno && state.entiDelegaImpostaSoggiorno.length > 0,
		getEntiDelegaImpostaSoggiorno(state) {
			return state.entiDelegaImpostaSoggiorno;
		},

		isEntiAbilitazioneStrutturaLoaded: (state) => state.entiAbilitazioneStruttura && state.entiAbilitazioneStruttura.length > 0,
		getEntiAbilitazioneStruttura(state) {
			return state.entiAbilitazioneStruttura;
		},

		isEntiAffissioneManifestiLoaded: (state) => state.entiAffissioneManifesti && state.entiAffissioneManifesti.length > 0,
		getEntiAffissioneManifesti(state) {
			return state.entiAffissioneManifesti;
		},

		isEntiPagamentoLoaded: (state) => state.entiPagamento && state.entiPagamento.length > 0,
		getEntiPagamento(state) {
			return state.entiPagamento;
		},
		isEntiPagamentoPrecompilatoLoaded: (state) => state.entiPagamentoPrecompilato && state.entiPagamentoPrecompilato.length > 0,
		getEntiPagamentoPrecompilato(state) {
			return state.entiPagamentoPrecompilato;
		},

		isPosizioniLoading: (state) => state.loadingPosizioni,
		isPosizioniLoaded: (state) => state.posizioni && state.posizioni.length > 0,
		getPosizioni(state) {
			return state.posizioni;
		},

		isDichiarazioniLoading: (state) => state.loadingDichiarazioni,
		isDichiarazioniLoaded: (state) => state.dichiarazioni && state.dichiarazioni.length > 0,
		getDichiarazioni(state) {
			return state.dichiarazioni;
		},

		isSegnalazioniLoaded: (state) => state.segnalazioni && state.segnalazioni.length > 0,
		getSegnalazioni(state) {
			return state.segnalazioni;
		},
		isEntiNuovoMezzoPermanenteLoaded: (state) => state.entiNuovoMezzoPermanente && state.entiNuovoMezzoPermanente.length > 0,
		getEntiNuovoMezzoPermanente(state) {
			return state.entiNuovoMezzoPermanente;
		},
		getEnteNuovoMezzoPermamenteById: (state) => (id: string, nome: string) => {
			//console.log('id:', id);
			const ente = state.entiNuovoMezzoPermanente.find(
				(ente) => {
					return ente.Ente__c == id && ente.idDescrizioneEntePortale__c == nome;
				}
			);

			return ente ? ente.idDescrizioneEntePortale__c : "";
		},
	},
	actions: {
		resetStore() {
			this.$reset(); // Resetta tutto lo store
		},
		async fetchTipiSegnalazione() {

			this.tipiSegnalazione = await this.apiInstance.getListTipiSegnalazione();
			//console.log(this.tipiSegnalazione);
		},

		async fetchEntiSportelloTerritoriale() {
			this.entiSportelloTerritoriale = await this.apiInstance.getSportelloEnti();
			//console.log(this.entiSportelloTerritoriale);
		},

		async fetchEntiNuovoAllacciamentoSegnalazione() {
			this.entiNuovoAllacciamentoSegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_ILLUMINAZIONE_VOTIVA
			);
			//console.log(this.entiNuovoAllacciamentoSegnalazione);
		},

		async fetchEntiRecuperoCreditiSegnalazione() {
			this.entiRecuperoCreditiSegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_RECUPERO_CREDITI
			);
			//console.log(this.entiRecuperoCreditiSegnalazione);
		},

		async fetchEntiParcheggiMobilitySegnalazione() {
			this.entiParcheggiMobilitySegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_MOBILITY
			);
			//console.log(this.entiParcheggiMobilitySegnalazione);
		},

		async fetchEntiPubblicitaAffissioniSegnalazione() {
			this.entiPubblicitaAffissioniSegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_PUBBLICITA
			);
			//console.log(this.entiPubblicitaAffissioniSegnalazione);
		},
		async fetchEntiRifiutiSegnalazione() {
			this.entiRifiutiSegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_RIFIUTI
			);
			//console.log(this.entiRifiutiSegnalazione);
		},
		async fetchEntiOccupazioneSuoloSegnalazione() {
			this.entiOccupazioneSuoloSegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_OCCUPAZIONE_SUOLO
			);
			//console.log(this.entiOccupazioneSuoloSegnalazione);
		},

		async fetchEntiImmobiliSegnalazione() {
			this.entiImmobiliSegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_IMMOBILI
			);
			//console.log(this.entiImmobiliSegnalazione);
		},

		async fetchEntiImpostaSoggiornoSegnalazione() {
			this.entiImpostaSoggiornoSegnalazione = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.SEGNALAZIONE_IMPOSTA_SOGGIORNO
			);
			//console.log(this.entiImpostaSoggiornoSegnalazione);
		},

		async fetchEntiNuovoAllacciamento() {
			this.entiNuovoAllacciamento = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.ILLUMINAZIONE_VOTIVA
			);
			//console.log(this.entiNuovoAllacciamento);
		},

		async fetchEntiAbbonamentoParcheggi() {
			this.entiAbbonamentoParcheggi = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.NUOVO_ABBONAMNETO_PARCHEGGI
			);
		},

		async fetchEntiAffissioneManifesti() {
			this.entiAffissioneManifesti = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.PUBBLICITA_E_PUBBLICHE_AFFISSIONI
			);
			//console.log(this.entiAffissioneManifesti);
		},

		async fetchEntiLocandine() {
			this.entiLocandine = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.LOCANDINE
			);
			//console.log(this.entiLocandine);
		},

		async fetchEntiIstanzaContribuente() {
			this.entiIstanzaContribuente = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.FUNZIONE_RICHIESTA_RIESAME_PRATICA
			);
			//console.log(this.entiIstanzaContribuente);
		},

		async fetchEntiDelegaImpostaSoggiorno() {
			this.entiDelegaImpostaSoggiorno = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.DELEGA_IMPOSTA_SOGGIORNO
			);
			//console.log(this.entiDelegaImpostaSoggiorno);
		},

		async fetchEntiAbilitazioneStruttura() {
			this.entiAbilitazioneStruttura = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.ABILITAZIONE_STRUTTURA_RICETTIVA
			);
		},

		async fetchEntiPagamento() {
			this.entiPagamento = await this.apiInstance.getElencoEntiPagamento();
			//console.log(this.entiPagamento);
		},
		async fetchEntiPagamentoPrecompilato() {
			this.entiPagamentoPrecompilato = await this.apiInstance.getElencoEntiPagamentoPrecompilato();
			//console.log(this.entiPagamentoPrecompilato);
		},

		async fetchPosizioni() {
			//console.log('sono dentro fetch posizioni');
			this.loadingPosizioni = true;
			this.posizioni = await this.apiInstance.getPosizioniList();
			//console.log(this.posizioni);
			this.loadingPosizioni = false;
		},
		async fetchDichiarazioni() {
			this.loadingDichiarazioni = true;
			this.dichiarazioni = await this.apiInstance.getDichiarazioniList();
			//console.log(this.dichiarazioni);
			this.loadingDichiarazioni = false;
		},
		async fetchSegnalazioni() {
			this.segnalazioni = await this.apiInstance.getSegnalazioniList();
			//console.log(this.segnalazioni);
		},
		async fetchEntiNuovoMezzoPermanente() {
			this.entiNuovoMezzoPermanente = await this.apiInstance.getEntiPerCategoria(
				LISTA_CATEGORIE_ENTE_CATEGORIA.NUOVO_MEZZO_PUBBLICITARIO
			);
			//console.log(this.entiNuovoMezzoPermanente);
		},
	},
});
